// The group layer: what a set is worth to a crew rather than to one person.
//
// Three objectives are available and the default is the third, because it is the
// only one a group will accept out loud.
//
//   utilitarian  the weighted sum. Fast, and its failure mode is systematic: the
//                member with the most opinions wins every contested slot and the
//                quieter one is steamrolled all weekend without any single
//                decision looking unfair.
//   nash         the product of member values, applied per set as a geometric
//                mean. Resists domination, but a product of utilities is not a
//                sentence anybody accepts as a reason to skip an artist.
//   ledger       the weighted sum where a member's weight rises with how much they
//                have already given up. Recommended, because "Carla gave way on
//                the last two, so her preference counts for more here" is a
//                sentence a group will accept, and an optimizer nobody accepts has
//                no fairness properties at all.
//
// Regret carries across days with mild decay, so a sacrifice on Friday still
// counts on Sunday and counts less than one made an hour ago.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import type { MemberView } from './normalize.ts';
import { valueFor } from './normalize.ts';
import { solveDay } from './solver.ts';
import type { RestRequirement, SolveResult } from './solver.ts';
import type { DayContext, FestivalSet, Id, LedgerRow, WorkspaceSettings } from './types.ts';

/** Turns accumulated regret into the multiplier each member carries into a decision. */
export function ledgerWeights(
  rows: LedgerRow[],
  cfg: TuningConfig = DEFAULT_CONFIG,
): Map<Id, number> {
  const out = new Map<Id, number>();
  if (rows.length === 0) return out;
  const mean = rows.reduce((a, r) => a + r.decayedRegret, 0) / rows.length;
  const [lo, hi] = cfg.ledgerWeightClamp;
  for (const r of rows) {
    if (mean <= 0) {
      out.set(r.userId, 1);
      continue;
    }
    const raw = 1 + cfg.ledgerAlpha * (r.decayedRegret / mean - 1);
    out.set(r.userId, Math.max(lo, Math.min(hi, raw)));
  }
  return out;
}

/** Applies one festival day of decay to every member's accumulated regret. */
export function decayLedger(rows: LedgerRow[], cfg: TuningConfig = DEFAULT_CONFIG): LedgerRow[] {
  return rows.map((r) => ({ ...r, decayedRegret: r.decayedRegret * cfg.ledgerDailyDecay }));
}

/** Charges each member the gap between what they wanted and what the crew chose. */
export function chargeRegret(
  rows: LedgerRow[],
  perMemberBest: Map<Id, number>,
  perMemberChosen: Map<Id, number>,
): LedgerRow[] {
  return rows.map((r) => {
    const best = perMemberBest.get(r.userId) ?? 0;
    const chosen = perMemberChosen.get(r.userId) ?? 0;
    const regret = Math.max(0, best - chosen);
    return {
      ...r,
      cumulativeRegret: r.cumulativeRegret + regret,
      decayedRegret: r.decayedRegret + regret,
    };
  });
}

export interface GroupUtilityInput {
  views: MemberView[];
  weights: Map<Id, number>;
  objective: WorkspaceSettings['groupObjective'];
  cfg?: TuningConfig;
}

/** Returns a member's comparable value for a set, or null when they have no view of it. */
export function memberValue(
  view: MemberView,
  artistId: Id,
): { value: number; weight: number } | null {
  const n = valueFor(view, artistId);
  if (!n) return null;
  return { value: n.value, weight: n.weight };
}

/** Combines member values for one set under the workspace's chosen objective. */
export function combineForSet(set: FestivalSet, input: GroupUtilityInput): number {
  const parts: { value: number; weight: number }[] = [];
  for (const view of input.views) {
    const mv = memberValue(view, set.artistId);
    if (!mv) continue;
    const ledger = input.weights.get(view.userId) ?? 1;
    // Coverage correction. A member with very few manual ratings carries less of
    // the group's opinion than one who has actually done the work.
    const coverage = Math.min(1, 0.5 + view.manualCount / 60);
    parts.push({ value: mv.value, weight: mv.weight * ledger * coverage });
  }
  if (parts.length === 0) return 0;

  const totalW = parts.reduce((a, p) => a + p.weight, 0);
  if (totalW <= 0) return 0;

  if (input.objective === 'nash') {
    // Per-set stand-in for Nash welfare: the weighted geometric mean. The real
    // objective is a product over whole schedules, which an additive DP cannot
    // carry, so this is the closest additive surrogate and is labeled as one.
    const logSum = parts.reduce((a, p) => a + p.weight * Math.log(Math.max(1e-6, p.value)), 0);
    return Math.exp(logSum / totalW);
  }

  return parts.reduce((a, p) => a + p.weight * p.value, 0) / totalW;
}

/** Builds the group utility map for one day's sets. */
export function groupUtilityMap(sets: FestivalSet[], input: GroupUtilityInput): Map<Id, number> {
  const out = new Map<Id, number>();
  for (const s of sets) out.set(s.id, combineForSet(s, input));
  return out;
}

export interface TogetherInput {
  ctx: DayContext;
  group: GroupUtilityInput;
  /** Every member's locks, because in together mode the crew moves as one. */
  hardLockSetIds: Id[];
  rest: RestRequirement[];
  homeStageId?: Id;
  cfg?: TuningConfig;
}

/** Solves the day for a crew moving as a single unit. */
export function solveTogether(input: TogetherInput): SolveResult {
  const utility = groupUtilityMap(input.ctx.sets, input.group);
  return solveDay({
    ctx: input.ctx,
    utility,
    hardLockSetIds: input.hardLockSetIds,
    rest: input.rest,
    homeStageId: input.homeStageId,
    cfg: input.cfg,
  });
}

/** Starts a ledger row for every member of a new workspace. */
export function newLedger(userIds: Id[], workspaceId: Id, settings: WorkspaceSettings): LedgerRow[] {
  return userIds.map((userId) => ({
    workspaceId,
    userId,
    cumulativeRegret: 0,
    decayedRegret: 0,
    tokensRemaining: settings.tokenAllocation,
  }));
}
